// src/api/separation.ts

import api from './api';
import {
  ResignationCreate,
  ResignationAction,
  ResignationWithdraw,
  EarlyReleaseAction,
  TerminationCreate,
  TerminationUpdate,
  ChecklistItemUpdate,
  SettlementCreate,
  SettlementAction,
} from '../types/separation';

// ==================== Resignation Endpoints ====================

export const submitResignation = async (payload: ResignationCreate) => {
  try {
    const response = await api.post('/separation/resign', payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const getResignations = async (params: {
  status?: string;
  employee_id?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const response = await api.get('/separation/resignations', { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const getResignationById = async (resignationId: string) => {
  try {
    const response = await api.get(`/separation/resignations/${resignationId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const actionResignation = async (resignationId: string, payload: ResignationAction) => {
  try {
    const response = await api.post(`/separation/resignations/${resignationId}/action`, payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const withdrawResignation = async (resignationId: string, payload: ResignationWithdraw) => {
  try {
    const response = await api.post(`/separation/resignations/${resignationId}/withdraw`, payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const earlyReleaseAction = async (resignationId: string, payload: EarlyReleaseAction) => {
  try {
    const response = await api.post(`/separation/resignations/${resignationId}/early-release`, payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

// ==================== Termination Endpoints ====================

export const terminateEmployee = async (payload: TerminationCreate) => {
  try {
    const response = await api.post('/separation/terminate', payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const getTerminations = async (params: {
  employee_id?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const response = await api.get('/separation/terminations', { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const getTerminationById = async (terminationId: string) => {
  try {
    const response = await api.get(`/separation/terminations/${terminationId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const updateTermination = async (terminationId: string, payload: TerminationUpdate) => {
  try {
    const response = await api.patch(`/separation/terminations/${terminationId}`, payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

// ==================== Exit Checklist Endpoints ====================

export const getChecklists = async (params: {
  status?: string;
  page?: number;
  limit?: number;
} = {}) => {
  try {
    const response = await api.get('/separation/checklists', { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const getExitChecklist = async (separationType: string, separationId: string) => {
  try {
    const response = await api.get(`/separation/checklist/${separationType}/${separationId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const updateChecklistItem = async (checklistId: string, payload: ChecklistItemUpdate) => {
  try {
    const response = await api.patch(`/separation/checklist/${checklistId}/item`, payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const assignChecklist = async (checklistId: string, assignedTo: string) => {
  try {
    const response = await api.post(`/separation/checklist/${checklistId}/assign`, null, {
      params: { assigned_to: assignedTo }
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

// ==================== Settlement Endpoints ====================

export const createSettlement = async (payload: SettlementCreate) => {
  try {
    const response = await api.post('/separation/settlement', payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const getSettlements = async (params: {
  status?: string;
  page?: number;
  limit?: number;
} = {}) => {
  try {
    const response = await api.get('/separation/settlements', { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const getSettlement = async (settlementId: string) => {
  try {
    const response = await api.get(`/separation/settlement/${settlementId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const settlementAction = async (settlementId: string, payload: SettlementAction) => {
  try {
    const response = await api.post(`/separation/settlement/${settlementId}/action`, payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

// ==================== Document Endpoints ====================

export const downloadRelievingLetter = async (separationType: string, separationId: string) => {
  try {
    const response = await api.get(`/separation/${separationType}/${separationId}/relieving-letter`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relieving_letter.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const downloadExperienceLetter = async (separationType: string, separationId: string) => {
  try {
    const response = await api.get(`/separation/${separationType}/${separationId}/experience-letter`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `experience_letter.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const downloadTerminationLetter = async (separationType: string, separationId: string) => {
  try {
    const response = await api.get(`/separation/${separationType}/${separationId}/termination-letter`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `termination_letter.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const sendRelievingLetterEmail = async (separationType: string, separationId: string) => {
  try {
    const response = await api.post(`/separation/${separationType}/${separationId}/send-relieving-letter`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const sendExperienceLetterEmail = async (separationType: string, separationId: string) => {
  try {
    const response = await api.post(`/separation/${separationType}/${separationId}/send-experience-letter`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export const sendTerminationLetterEmail = async (separationType: string, separationId: string) => {
  try {
    const response = await api.post(`/separation/${separationType}/${separationId}/send-termination-letter`);
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

// ==================== Dashboard Stats ====================

export const getSeparationStats = async () => {
  try {
    const response = await api.get('/separation/dashboard/stats');
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error.message);
  }
};